import { makeArticle } from "./learnFactory";

const comparisonRelated = [
  {
    title: "SAINT® Accelerated TMS",
    href: "/saint",
    description: "The 5-day, fMRI-guided protocol",
  },
  {
    title: "SPRAVATO® (Esketamine)",
    href: "/services/spravato",
    description: "FDA-approved nasal spray for TRD",
  },
  {
    title: "SAINT® vs SPRAVATO®",
    href: "/learn/saint-vs-spravato",
    description: "Device vs medication comparison",
  },
  {
    title: "Compare Treatments",
    href: "/compare-treatments",
    description: "All treatment options compared",
  },
  {
    title: "Schedule a Consultation",
    href: "/contactus",
    description: "Discuss which treatment fits you",
  },
];

export const pageTmsVsSpravato = makeArticle({
  slug: "tms-vs-spravato",
  title:
    "TMS vs SPRAVATO® for Depression: What's the Difference? | Neuro Wellness",
  description:
    "TMS vs SPRAVATO® (esketamine) compared: non-invasive brain stimulation vs nasal spray medication. FDA status, schedule, monitoring, side effects, and insurance.",
  canonical: "/learn/tms-vs-spravato",
  h1: "TMS Therapy vs SPRAVATO®: Understanding the Differences",
  breadcrumbLabel: "TMS vs SPRAVATO",
  shortAnswer:
    "TMS and SPRAVATO® (esketamine) are both treatments for depression that has not responded adequately to antidepressant medication, but they work through entirely different mechanisms. TMS is a non-invasive brain stimulation device that uses magnetic pulses to stimulate a specific brain region — no medication, no sedation, and patients drive themselves home. SPRAVATO® is a nasal spray medication (esketamine) that causes dissociation and sedation during a two-hour monitored session and requires a ride home. TMS devices are FDA-cleared; SPRAVATO® is FDA-approved. Both are options for treatment-resistant depression. Neither guarantees results, and the appropriate choice depends on clinical circumstances.",
  keyTakeaways: [
    "TMS is a non-invasive device; SPRAVATO® is a medication (esketamine nasal spray).",
    "TMS requires no sedation; SPRAVATO® causes dissociation and requires two-hour monitoring.",
    "TMS devices are FDA-cleared; SPRAVATO® is FDA-approved.",
    "TMS patients can drive home; SPRAVATO® patients cannot drive until the next day.",
    "Both are options for treatment-resistant depression.",
  ],
  sections: [
    {
      heading: "How They Work",
      paragraphs: [
        "TMS (transcranial magnetic stimulation) uses magnetic pulses to stimulate the left dorsolateral prefrontal cortex — a brain region involved in mood regulation. The magnetic field passes through the scalp and skull, inducing small electrical currents in the cortex. TMS introduces no chemical into the body and does not require anesthesia. Patients sit awake in a chair during treatment.",
        "SPRAVATO® (esketamine) is an NMDA-receptor-blocking nasal spray medication. It is self-administered under supervision at a certified clinic. Esketamine causes dissociation — a feeling of being detached from one's body or surroundings — as well as sedation and a temporary rise in blood pressure. After administration, patients must be monitored for at least two hours before discharge.",
      ],
    },
    {
      heading: "Key Differences",
      paragraphs: [],
      table: {
        headers: ["Feature", "TMS", "SPRAVATO® (Esketamine)"],
        rows: [
          ["Type", "Non-invasive device", "Medication (nasal spray)"],
          [
            "Mechanism",
            "Magnetic brain stimulation",
            "NMDA receptor antagonist",
          ],
          ["FDA status", "FDA-cleared", "FDA-approved"],
          ["Anesthesia/sedation", "None", "Dissociation and sedation"],
          [
            "Session length",
            "~3–40 minutes",
            "~2.5–3 hours (incl. monitoring)",
          ],
          ["Driving after", "Yes", "No (not until next day)"],
          [
            "Monitoring required",
            "None after session",
            "At least 2 hours after each dose",
          ],
          ["Setting", "Outpatient clinic", "REMS-certified clinic"],
          [
            "Common side effects",
            "Headache, scalp discomfort",
            "Dissociation, dizziness, nausea, sedation",
          ],
          [
            "Insurance",
            "Often covered (with criteria)",
            "Often covered (with prior auth)",
          ],
        ],
      },
    },
    {
      heading: "Treatment Schedule",
      paragraphs: [
        "Conventional TMS is delivered as one session per day, five days a week, for about 4–6 weeks — roughly 36 sessions total. Each session lasts from about 3 minutes (theta burst) to about 40 minutes (10 Hz protocol). Patients can drive themselves to and from sessions and return to normal activities immediately.",
        "SPRAVATO® is delivered in two phases. The induction phase involves twice-weekly sessions for four weeks, then weekly sessions, then every one to two weeks. Each visit takes about 2.5–3 hours including pre-dose blood pressure checks, self-administration, and at least two hours of required monitoring. Patients must have a driver and cannot drive until the next day after a restful sleep.",
      ],
    },
    {
      heading: "Who May Be a Candidate",
      paragraphs: [
        "TMS is FDA-cleared for adults with major depressive disorder who have not achieved satisfactory improvement from prior antidepressant medication in the current episode. TMS is non-invasive and does not introduce medication into the body, which may appeal to patients who prefer a drug-free approach or who have not tolerated antidepressant side effects.",
        "SPRAVATO® is FDA-approved for treatment-resistant depression in adults, either as monotherapy or with an oral antidepressant, and for depressive symptoms in adults with major depressive disorder who have acute suicidal thoughts or behavior. SPRAVATO® requires screening for contraindications including aneurysmal vascular disease, arteriovenous malformation, and prior brain hemorrhage.",
        "A psychiatric evaluation determines which treatment is appropriate for a specific person. Some patients may be candidates for both, and the choice depends on treatment history, preferences, insurance coverage, and clinical circumstances.",
      ],
    },
    {
      heading: "Insurance Considerations",
      paragraphs: [
        "Conventional TMS is broadly covered by commercial insurance, Medicare, and the VA for treatment-resistant depression when medical-necessity criteria are met, such as documented failed antidepressant trials and psychiatrist oversight. Prior authorization is commonly required.",
        "SPRAVATO® is often covered by commercial plans and Medicare for its FDA-approved indications after prior authorization. The drug must be administered at a REMS-certified site, and plans may bill the drug and monitoring separately. Coverage and copays vary by plan.",
        "Neither treatment guarantees insurance coverage. Benefits verification before starting treatment is essential. Neuro Wellness TMS Centers of America can verify benefits but cannot promise coverage.",
      ],
    },
    {
      heading: "Major Limitations",
      paragraphs: [
        "TMS does not work for everyone — roughly 40% or more of patients in real-world series do not meet response criteria, and some who improve later relapse. TMS requires a significant time commitment for conventional courses (4–6 weeks of daily visits).",
        "SPRAVATO® causes dissociation and sedation during each session, requires a driver, and involves ongoing monitoring. Long-term safety data beyond one year is limited. SPRAVATO® is a Schedule III controlled substance and is administered only under a REMS program.",
      ],
    },
    {
      heading: "Questions to Ask a Clinician",
      paragraphs: [],
      list: [
        "Am I a candidate for TMS, SPRAVATO®, or both?",
        "What are the specific risks and side effects for my situation?",
        "How long is each treatment course, and how many visits are required?",
        "Will my insurance cover this treatment, and what will my out-of-pocket cost be?",
        "What happens if the first treatment does not work?",
        "Can I combine TMS and SPRAVATO® with my current medication?",
        "What follow-up and maintenance are needed after treatment?",
      ],
    },
  ],
  faqs: [
    {
      question: "Is TMS better than SPRAVATO?",
      answer:
        "Neither is universally better. TMS is non-invasive and does not require medication or sedation, while SPRAVATO® works through a different mechanism and may act more rapidly for some patients. The appropriate choice depends on treatment history, preferences, contraindications, and clinical circumstances. A psychiatric evaluation can help determine which is right for you.",
    },
    {
      question: "Can I drive after SPRAVATO treatment?",
      answer:
        "No. SPRAVATO® (esketamine) causes dissociation and sedation, and patients must be monitored for at least two hours after each dose. Patients cannot drive until the next day after a restful sleep. A driver is required for every SPRAVATO appointment. TMS, by contrast, does not impair driving.",
    },
    {
      question: "Does SPRAVATO require anesthesia?",
      answer:
        "No, SPRAVATO® does not require general anesthesia. It is a nasal spray that causes dissociation and sedation as expected effects. However, the dissociation and blood pressure changes require monitoring in a certified clinic for at least two hours after each dose.",
    },
    {
      question: "Is TMS FDA-approved or FDA-cleared?",
      answer:
        "TMS devices are FDA-cleared, not FDA-approved. 'Clearance' is the regulatory pathway for medical devices; 'approval' is the pathway for drugs. SPRAVATO® (esketamine) is FDA-approved because it is a medication. Both regulatory pathways require the FDA to review safety and effectiveness for specific indications.",
    },
    {
      question: "Can I take SPRAVATO and do TMS?",
      answer:
        "Some patients may receive both treatments, but this is a clinical decision made by a psychiatrist. The two work through different mechanisms and may be used in sequence or combination depending on individual circumstances. Discuss your full treatment history with your clinician.",
    },
    {
      question: "Which is faster — TMS or SPRAVATO?",
      answer:
        "SPRAVATO® may produce more rapid effects for some patients, with some improvement noted within hours to days. Conventional TMS typically takes 2–4 weeks to show noticeable improvement. Accelerated TMS protocols like SAINT® deliver the full course in five days. Individual response varies and neither treatment guarantees a specific timeline.",
    },
  ],
  sources: [
    {
      citation: "U.S. FDA. SPRAVATO (esketamine) prescribing information.",
      url: "https://www.accessdata.fda.gov/drugsatfda_docs/label/2025/211243s019lbl.pdf",
    },
    {
      citation:
        "O'Reardon JP, et al. Efficacy and safety of transcranial magnetic stimulation in the acute treatment of major depression. Biol Psychiatry. 2007;62(11):1208-1216.",
      url: "https://pubmed.ncbi.nlm.nih.gov/17573044/",
    },
  ],
  relatedLinks: comparisonRelated,
});
